import React from "react";
import { Box, Typography, Button } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import { safeJSONParse } from "../../../utils/helper";
import MarkdownRenderer from "../../MarkdownRenderer";

type TemplateGuidedIntakeAgentProps = {
  parsedContent: any;
};

// MIME types for supported document formats
const MIME_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

// Get file extension from filename
const getFileExtension = (filename: string): string => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  return ext;
};

export default function TemplateGuidedIntakeAgent({
  parsedContent,
}: TemplateGuidedIntakeAgentProps): JSX.Element {
  const finalAnswer = safeJSONParse(parsedContent?.finalAnswer);

  // Extract question from nested structure
  const question =
    finalAnswer?.payload?.result?.data?.question ||
    finalAnswer?.result?.data?.question ||
    finalAnswer?.question ||
    parsedContent?.question ||
    "";

  // Extract document data from multiple possible locations
  // Check in response[0].result.payload first (new structure)
  const responsePayload = finalAnswer?.payload?.result?.data?.response?.[0]?.result?.payload;

  // PDF data extraction
  const pdfUrl =
    responsePayload?.pdf_url ||
    finalAnswer?.payload?.pdf_url ||
    finalAnswer?.pdf_url ||
    parsedContent?.pdf_url;

  const pdfBase64 =
    responsePayload?.pdf_base64 ||
    finalAnswer?.payload?.pdf_base64 ||
    finalAnswer?.pdf_base64 ||
    parsedContent?.pdf_base64;

  const pdfFilename =
    responsePayload?.pdf_filename ||
    finalAnswer?.payload?.pdf_filename ||
    finalAnswer?.pdf_filename ||
    parsedContent?.pdf_filename;

  // DOCX data extraction
  const docxUrl =
    responsePayload?.docx_url ||
    finalAnswer?.payload?.docx_url ||
    finalAnswer?.docx_url ||
    parsedContent?.docx_url;

  const docxBase64 =
    responsePayload?.docx_base64 ||
    finalAnswer?.payload?.docx_base64 ||
    finalAnswer?.docx_base64 ||
    parsedContent?.docx_base64;

  const docxFilename =
    responsePayload?.docx_filename ||
    finalAnswer?.payload?.docx_filename ||
    finalAnswer?.docx_filename ||
    parsedContent?.docx_filename;

  // Extract filename from question text if not provided separately (supports both .pdf and .docx)
  const filenameMatch = question.match(/\(([^)]+\.(pdf|docx))\)/i);
  const extractedFilename = filenameMatch ? filenameMatch[1] : null;

  // Determine file type from explicit filename fields or extracted filename
  const explicitFilename = docxFilename || pdfFilename;
  const detectedFileType = explicitFilename
    ? getFileExtension(explicitFilename)
    : extractedFilename
      ? getFileExtension(extractedFilename)
      : null;

  // Determine which file type is available
  // Note: Backend may send DOCX data using pdf_* fields, so we check filename extension first
  const isDocxByFilename = detectedFileType === 'docx';
  const hasDocxData = docxUrl || docxBase64;
  const hasPdfData = pdfUrl || pdfBase64;

  // Determine file type: prioritize filename detection, then fall back to available data
  const fileType = isDocxByFilename ? 'docx' : (hasDocxData ? 'docx' : (hasPdfData ? 'pdf' : 'pdf'));
  const fileTypeLabel = fileType.toUpperCase();

  // Get the appropriate URL, base64, and filename based on file type
  // If file type is DOCX but data is in PDF fields (backend compatibility), use PDF field data
  const fileUrl = docxUrl || pdfUrl;
  const fileBase64 = docxBase64 || pdfBase64;
  const fileFilename = docxFilename || pdfFilename;

  if (!question) {
    return <Typography>No data available</Typography>;
  }

  // Function to download document from base64
  const downloadFromBase64 = (base64String: string, filename: string) => {
    try {
      // Clean the base64 string - remove whitespace, newlines
      let cleanedBase64 = base64String
        .replace(/\s+/g, '') // Remove all whitespace
        .replace(/\n/g, '') // Remove newlines
        .replace(/\r/g, ''); // Remove carriage returns

      // Try to decode if URL-encoded
      try {
        cleanedBase64 = decodeURIComponent(cleanedBase64);
      } catch (e) {
        // If decoding fails, use the cleaned string as-is
      }

      // Decode base64 to binary
      const fileBytes = atob(cleanedBase64);
      const byteArray = new Uint8Array(fileBytes.length);
      for (let i = 0; i < fileBytes.length; i++) {
        byteArray[i] = fileBytes.charCodeAt(i);
      }

      // Determine MIME type based on file extension
      const ext = getFileExtension(filename);
      const mimeType = MIME_TYPES[ext] || 'application/octet-stream';

      const blob = new Blob([byteArray], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  // Function to handle document download
  const handleDownload = () => {
    const defaultFilename = `filled_template.${fileType}`;
    const filename = fileFilename || extractedFilename || defaultFilename;

    if (fileBase64) {
      downloadFromBase64(fileBase64, filename);
    } else if (fileUrl) {
      window.open(fileUrl, '_blank');
    } else {
      alert('File is not available. Please contact support.');
    }
  };

  // Remove document reference from question text for cleaner display (supports both PDF and DOCX)
  const cleanQuestion = question.replace(
    /\*\*Filled Template (PDF|DOCX|Document):\*\*[^\n]*\([^)]+\.(pdf|docx)\)[^\n]*/i,
    ''
  );

  return (
    <Box>
      {/* Document Download Section */}
      {extractedFilename && (
        <Box
          sx={{
            marginBottom: "20px",
            padding: "16px",
            backgroundColor: "#f0fbf9",
            borderRadius: "6px",
            border: "1px solid #B2E5DE",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <DescriptionIcon sx={{ fontSize: 32, color: "#00a888" }} />
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#004141", marginBottom: "4px" }}
              >
                Filled Template {fileTypeLabel}
              </Typography>
              <Typography variant="body2" sx={{ color: "#403f3f", fontSize: "13px" }}>
                {fileFilename || extractedFilename}
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            startIcon={<DescriptionIcon />}
            onClick={handleDownload}
            disabled={!fileUrl && !fileBase64}
            sx={{
              backgroundColor: fileUrl || fileBase64 ? "#00a888" : "#ccc",
              color: "white",
              textTransform: "none",
              "&:hover": {
                backgroundColor: fileUrl || fileBase64 ? "#008a70" : "#ccc",
              },
            }}
          >
            {fileUrl || fileBase64 ? `Download ${fileTypeLabel}` : `${fileTypeLabel} Not Available`}
          </Button>
        </Box>
      )}

      {/* Markdown Content */}
      <MarkdownRenderer content={cleanQuestion || question} />
    </Box>
  );
}
